import { EnvelopeDocument } from '../types/envelopePage'

export function queryBuilder<QueryType>(queryObj: QueryType): string {
  return Object.keys(queryObj)
    .map((key) => {
      const value = queryObj[key]

      return `${key}=${Array.isArray(value) ? value.join(',') : value}`
    })
    .join('&')
}

export const random32BitInteger = (): number => {
  const maxInt = 2147483647

  return Math.floor(Math.random() * maxInt) + 1
}

export const getMIMETypeFromFileExtension = (extension: string) => {
  /**
   * Reference: https://www.ibm.com/docs/en/wkc/cloud?topic=catalog-previews
   * @param extension Receives value in form of .<extension> (including dot ".")
   */

  let ext = extension.includes('.') ? extension : `.${extension}`
  ext = ext.toLowerCase()
  switch (ext) {
    /* document */
    case '.doc':
    case '.dot':
      return 'application/msword'
    case '.docm':
      return 'application/vnd.ms-word.document.macroEnabled.12'
    case '.docx':
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    case '.dotm':
      return 'application/vnd.ms-word.template.macroEnabled.12'
    case '.dotx':
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.template'
    case '.htm':
    case '.html':
      return 'text/html'
    case '.msg':
      return 'application/vnd.ms-outlook'
    case '.pdf':
      return 'application/pdf'
    case '.rtf':
      return 'application/rtf'
    case '.txt':
      return 'text/plain'
    case '.wpd':
      return 'application/wordperfect'
    case '.xps':
      return 'application/vnd.ms-xpsdocument'
    case '.xlsx':
      return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    case '.xls':
      return 'application/vnd.ms-excel'
    case '.xlsm':
      return 'application/vnd.ms-excel.sheet.macroEnabled.12'
    case '.csv':
      return 'text/csv'

    /* image */
    case '.bmp':
      return 'image/bmp'
    case '.jpeg':
    case '.jpg':
      return 'image/jpeg'
    case '.png':
      return 'image/png'
    case '.gif':
      return 'image/gif'
    case '.tif':
    case '.tiff':
      return 'image/tiff'

    /* presentation */
    case '.pot':
    case '.pps':
    case '.ppt':
      return 'application/mspowerpoint'
    case '.potx':
      return 'application/vnd.openxmlformats-officedocument.presentationml.template'
    case '.pptm':
      return 'application/vnd.ms-powerpoint.template.macroEnabled.12'
    case '.pptx':
      return 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
  }
}

export const DOCUSIGN_SUPPORTED_DOCUMENT_TYPES = [
  /* document */
  '.doc',
  '.docm',
  '.docx',
  '.dot',
  '.dotm',
  '.dotx',
  '.htm',
  '.html',
  '.msg',
  '.pdf',
  '.rtf',
  '.txt',
  '.wpd',
  '.xps',

  /* image */
  '.bmp',
  '.gif',
  '.jpg',
  '.jpeg',
  '.png',
  '.tif',
  '.tiff',

  /* presentation*/
  '.pot',
  '.potx',
  '.pps',
  '.ppt',
  '.pptm',
  '.pptx',

  /* spreadsheet */
  '.csv',
  '.xls',
  '.xlsm',
  '.xlsx',
]

export const documentTypeSupportedByDocusign = (extension: string) => {
  let ext = extension.includes('.') ? extension : `.${extension}`
  ext = ext.toLowerCase()
  return DOCUSIGN_SUPPORTED_DOCUMENT_TYPES.includes(ext)
}

export const getFileExtensionFromFileName = (name: string) => {
  return name.split('.').pop() ?? ''
}
export const pluralize = (count: number, noun: string, suffix = 's') => {
  return `${count} ${noun}${count !== 1 ? suffix : ''}`
}

export const INPUT_ACCEPTED_FILE_EXTENSIONS =
  '.doc, .docm, .docx, .dot, .dotm, .dotx, .htm, .html, .msg, .pdf, .rtf, .txt, .wpd, .xps, .bmp, .gif, .jpg, .jpeg, .png, .tif, .tiff, .pot, .potx, .pps, .ppt, .pptm, .pptx .pot, .potx, .pps, .ppt, .pptm, .pptx, .csv, .xls, .xlsm, .xlsx'

export function omitProp<ObjType, Prop extends keyof ObjType>(obj: ObjType, ommittedProp: Prop[]): ObjType {
  const newObj = { ...obj }

  ommittedProp.forEach((prop) => newObj[prop] && delete newObj[prop])

  return newObj
}

export const readUploadedFileAsBase64 = (file: File, isWithoutHeader?: boolean) => {
  const tempFileReader = new FileReader()

  return new Promise((resolve, reject) => {
    tempFileReader.onerror = () => {
      tempFileReader.abort()
      reject(new DOMException(`Problem while parsing file: ${file.name}`))
    }

    tempFileReader.onload = () => {
      const fileContent = String(tempFileReader.result)
      resolve(isWithoutHeader ? fileContent.split('base64,')[1] || '' : fileContent)
    }

    tempFileReader.readAsDataURL(file)
  })
}

export const getEmailSubject = (documents: EnvelopeDocument[]) => {
  let title = documents.length > 0 ? `Please DocuSign: ${documents.map((doc) => doc.name).join(', ')}` : ''

  if (title.length > 100) {
    title = title.substring(0, 100)
    title = title.slice(0, -3) + '...'
  }

  return title
}

export const removeSalutation = (name: string): string => {
  const salutationRegex = /(Mr|MR|Ms|Miss|Mrs|Dr|Sir)(\.?)\s/
  const match = salutationRegex.exec(name)

  return match && Array.isArray(match) ? name.replace(match[0], '') : name
}
